import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ResponsiveNotePhoto } from "@rebuild/shared/models";
import { getRebuildAPI } from "@rebuild/shared/api";
import { RESPONSIVE_NOTE_PHOTO_KEYS } from "@/hooks/queries/responsive-note-photos/queryKeys";

const { storageControllerDeletePhotos } = getRebuildAPI();

interface DeleteResponsiveNotePhotosMutationParams {
  estimateId: string;
  photos: { noteId: string; photoId: string }[];
}

export function useDeleteResponsiveNotePhotosMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    // The route declares no response model, so the generated client resolves a Blob rather than an
    // `{ success }` envelope; a non-2xx rejects instead, which is what signals failure here.
    mutationFn: async (params: DeleteResponsiveNotePhotosMutationParams) => {
      await storageControllerDeletePhotos({
        estimateId: params.estimateId,
        photos: params.photos,
      });
    },
    // The toolbar narrows the selection before awaiting, so drop the tiles up front or the grid
    // keeps showing photos the user has already confirmed deleting until the refetch lands.
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });

      const previousPhotos = queryClient.getQueryData<ResponsiveNotePhoto[]>(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId));

      if (previousPhotos) {
        const photoKeys = new Set(variables.photos.map((photo) => `${photo.noteId}:${photo.photoId}`));
        queryClient.setQueryData(
          RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
          previousPhotos.filter((photo) => !photoKeys.has(`${photo.noteId}:${photo.id}`)),
        );
      }

      return { previousPhotos };
    },
    onError: (_error, variables, context) => {
      if (context?.previousPhotos) {
        queryClient.setQueryData(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId), context.previousPhotos);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });
    },
  });
}
